<script lang="ts">
    import { onDestroy } from "svelte";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import { locale } from "../../../../i18n/i18n-svelte";
    import RangeSlider from "../../Input/RangeSlider.svelte";
    import {
        MANUAL_MICROPHONE_GAIN_STEP,
        MAX_MANUAL_MICROPHONE_GAIN,
        MIN_MANUAL_MICROPHONE_GAIN,
    } from "../../../Stores/MicrophoneGainConfig";
    import { microphoneGainStore, type MicrophoneGainMode } from "../../../Stores/MicrophoneGainStore";
    import { noiseReductionStore } from "../../../Stores/NoiseReductionStore";
    import SectionTitle from "./SectionTitle.svelte";

    const MANUAL_GAIN_ANALYTICS_DEBOUNCE_MS = 400;

    let manualGainAnalyticsTimeout: ReturnType<typeof setTimeout> | undefined;
    let lastTrackedManualGain: number | undefined;

    function selectMode(mode: MicrophoneGainMode): void {
        if ($microphoneGainStore.mode === mode) {
            return;
        }

        microphoneGainStore.setMode(mode);
        analyticsClient.microphoneGainModeChanged(mode, $noiseReductionStore.provider);
    }

    function updateManualGain(value: number): void {
        microphoneGainStore.setManualGain(value);

        if (manualGainAnalyticsTimeout) {
            clearTimeout(manualGainAnalyticsTimeout);
        }

        manualGainAnalyticsTimeout = setTimeout(() => {
            const nextManualGain = $microphoneGainStore.manualGain;

            if (lastTrackedManualGain === nextManualGain) {
                return;
            }

            lastTrackedManualGain = nextManualGain;
            analyticsClient.microphoneManualGainChanged(nextManualGain, $noiseReductionStore.provider);
        }, MANUAL_GAIN_ANALYTICS_DEBOUNCE_MS);
    }

    $: currentMode = $microphoneGainStore.mode;
    $: effectiveMode = $microphoneGainStore.effectiveMode;
    $: currentManualGain = $microphoneGainStore.manualGain;
    $: status = $microphoneGainStore.status;
    $: fallbackReason = $microphoneGainStore.fallbackReason;
    $: isAutomaticUnavailable = currentMode === "automatic" && status === "unavailable" && fallbackReason !== null;
    $: isManualWarning =
        currentMode === "manual" && (status === "fallback" || status === "unavailable") && fallbackReason !== null;
    $: labels =
        $locale === "pt-BR"
            ? {
                  title: "Ajuste de ganho do microfone",
                  providerDefault: "Padrao atual",
                  automatic: "Automatico",
                  manual: "Manual",
                  manualGain: "Ganho manual",
                  manualHint: "Aplica ganho apos o redutor de ruido sem recriar a captura do microfone.",
                  active: "Ativo",
                  fallback: "Fallback",
                  unavailable: "Indisponivel",
              }
            : {
                  title: "Microphone gain",
                  providerDefault: "Default behavior",
                  automatic: "Automatic",
                  manual: "Manual",
                  manualGain: "Manual gain",
                  manualHint: "Applies gain after noise reduction without recreating microphone capture.",
                  active: "Active",
                  fallback: "Fallback",
                  unavailable: "Unavailable",
              };

    $: isActiveMode = (mode: MicrophoneGainMode) => effectiveMode === mode && status === "active";

    onDestroy(() => {
        if (manualGainAnalyticsTimeout) {
            clearTimeout(manualGainAnalyticsTimeout);
        }
    });
</script>

<div class="flex flex-col gap-1">
    <!-- svelte-ignore a11y-label-has-associated-control -->
    <SectionTitle title={labels.title} />

    <div class="flex flex-col gap-0.5 px-2">
        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentMode === 'provider-default'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectMode("provider-default")}
            title={labels.providerDefault}
        >
            <span
                class="w-2 h-2 rounded-full flex-shrink-0 {currentMode === 'provider-default'
                    ? 'bg-blue-400'
                    : 'bg-white/20'}"
            ></span>
            <span>{labels.providerDefault}</span>
            {#if isActiveMode("provider-default")}
                <span class="ml-auto text-xs text-green-400">{labels.active}</span>
            {/if}
        </button>

        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentMode === 'automatic'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectMode("automatic")}
            title={labels.automatic}
        >
            <span
                class="w-2 h-2 rounded-full flex-shrink-0 {currentMode === 'automatic'
                    ? 'bg-blue-400'
                    : 'bg-white/20'}"
            ></span>
            <span>{labels.automatic}</span>
            {#if isActiveMode("automatic")}
                <span class="ml-auto text-xs text-green-400">{labels.active}</span>
            {:else if isAutomaticUnavailable}
                <span class="ml-auto text-xs text-yellow-300">{labels.unavailable}</span>
            {/if}
        </button>

        <button
            class="flex items-center gap-2 py-1 px-1 rounded text-sm text-left w-full transition-colors
                {currentMode === 'manual'
                ? 'bg-white/15 text-white'
                : 'text-white/70 hover:bg-white/10 hover:text-white'}"
            on:click={() => selectMode("manual")}
            title={labels.manual}
        >
            <span
                class="w-2 h-2 rounded-full flex-shrink-0 {currentMode === 'manual'
                    ? 'bg-blue-400'
                    : 'bg-white/20'}"
            ></span>
            <span>{labels.manual}</span>
            {#if isActiveMode("manual")}
                <span class="ml-auto text-xs text-green-400">{labels.active}</span>
            {:else if currentMode === "manual" && status === "fallback"}
                <span class="ml-auto text-xs text-yellow-300">{labels.fallback}</span>
            {:else if currentMode === "manual" && status === "unavailable"}
                <span class="ml-auto text-xs text-yellow-300">{labels.unavailable}</span>
            {/if}
        </button>
    </div>

    {#if currentMode === "manual"}
        <div class="px-2 pt-1">
            <RangeSlider
                min={MIN_MANUAL_MICROPHONE_GAIN}
                max={MAX_MANUAL_MICROPHONE_GAIN}
                step={MANUAL_MICROPHONE_GAIN_STEP}
                value={currentManualGain}
                onChange={updateManualGain}
                label={labels.manualGain}
                valueFormatter={(value) => Math.round(value * 100).toString()}
                unit="%"
                variant="secondary"
                wrapperMargins={false}
            />
            <p class="text-[11px] text-white/55 mt-1 px-1">{labels.manualHint}</p>
        </div>
    {/if}

    {#if isAutomaticUnavailable || isManualWarning}
        <div class="mx-2 px-2 py-1 rounded bg-yellow-900/40 border border-yellow-600/40">
            <p class="text-xs text-yellow-300 break-words">{fallbackReason}</p>
        </div>
    {/if}
</div>
